import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, Divider, Link, Stack, Typography } from '@mui/material';
import Logo from '../../components/ui/Logo';
import { CoverageMeter } from '../../components/files/vaultUI';
import { nav, status as statusTokens } from '../../theme';

// One frame for every screen you can reach without being signed in.
//
// There were four, in three treatments: Login centred a white box with
// boxShadow={3} on #f5f5f5, AcceptInvite sat on a purple gradient
// (#667eea → #764ba2) that appears nowhere else in the product and belongs
// to no palette in it, and the two password screens had no background
// treatment at all — just a Container floating on the body colour.
//
// It is a split now: the product on the left, the form on the right. The
// left panel is deliberately NOT a stock photograph of a warehouse. It is
// built from the vault's own CoverageMeter, ObligationPill and StatusDot,
// so it shows the real thing at the real fidelity and cannot drift from
// the product the way a screenshot or a mood image would. It also means
// there is no image to load before somebody can sign in.
//
// The panel is hidden below `md`. On a phone, signing in is the whole job.

// A morning as the engine sees it: what it started, what it is waiting on,
// and the one step nobody has done yet.
const RUNS = [
  { label: 'Daily open', waiting: 'Finished', at: '06:00', tone: statusTokens.ok },
  { label: 'Start-of-shift check', waiting: 'Waiting on Dana W.', at: '07:15', tone: statusTokens.warn },
  { label: 'Return & issue triage', waiting: 'Escalated — no response', at: '07:40', tone: statusTokens.danger },
  { label: 'Day end report', waiting: 'Scheduled', at: '20:00', tone: 'idle' as const },
];

const Showcase: React.FC = () => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'space-between',
      bgcolor: nav.bg,
      px: { md: 6, lg: 9 },
      py: { md: 6, lg: 7 },
      // A single soft bloom behind the card, so the panel has some depth
      // without a gradient that fights the product's flat surfaces.
      backgroundImage: `radial-gradient(120% 80% at 15% 0%, rgba(51,146,255,0.16) 0%, rgba(51,146,255,0) 60%)`,
    }}
  >
    <Link component={RouterLink} to="/" sx={{ display: 'flex', width: 'fit-content' }} aria-label="TopoSDS home">
      <Logo height={30} reverse />
    </Link>

    <Box sx={{ maxWidth: 520, py: 5 }}>
      <Typography
        variant="h1"
        sx={{ color: '#fff', fontSize: { md: '2.25rem', lg: '2.75rem' }, lineHeight: 1.12, mb: 2 }}
      >
        Run the whole operation from one screen.
      </Typography>
      <Typography sx={{ color: nav.text, fontSize: 17, lineHeight: 1.65, mb: 5 }}>
        The engine knows what the day requires, tells every person what to do and when, and keeps the
        proof.
      </Typography>

      {/* What the panel shows has to match what the line above it claims.
          It used to be the compliance meter, which sold the filing cabinet
          directly underneath a headline about the engine. This is a run in
          flight: what the day started, who each step is waiting on, and the
          paperwork line underneath — the vault as one of the things the
          engine watches rather than the whole story. */}
      <Card sx={{ p: 3, maxWidth: 420, boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.disabled">Today · Area 3</Typography>
          <Typography variant="caption" color="text.secondary">4 running</Typography>
        </Stack>

        <Stack gap={1.75}>
          {RUNS.map((run) => (
            <Stack key={run.label} direction="row" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  ...(run.tone === 'idle'
                    ? { border: '1.5px solid', borderColor: 'grey.300' }
                    : { bgcolor: run.tone }),
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{run.label}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{run.waiting}</Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
              >
                {run.at}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {/* CoverageMeter prints its own figure — a hand-rolled label row
            above it showed 94% twice. */}
        <CoverageMeter
          score={{ percent: 94, satisfied: 276, total: 294 }}
          label="Paperwork on file"
        />
      </Card>
    </Box>

    <Stack direction="row" gap={3}>
      {[
        { to: '/support', label: 'Support' },
        { to: '/privacy', label: 'Privacy' },
        { to: '/terms', label: 'Terms' },
      ].map((link) => (
        <Link
          key={link.to}
          component={RouterLink}
          to={link.to}
          underline="hover"
          variant="body2"
          sx={{ color: nav.textMuted, '&:hover': { color: '#fff' } }}
        >
          {link.label}
        </Link>
      ))}
    </Stack>
  </Box>
);

const AuthLayout: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** A wider form column, for the invite flow. */
  wide?: boolean;
}> = ({ title, subtitle, children, footer, wide }) => (
  <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
    <Showcase />

    <Box
      sx={{
        width: { xs: '100%', md: wide ? 620 : 520 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: { md: '1px solid' },
        borderColor: { md: 'divider' },
        px: { xs: 3, sm: 6 },
        py: { xs: 5, md: 6 },
        overflowY: 'auto',
      }}
    >
      {/* The panel is hidden on a phone, so the wordmark has to appear
          here instead — otherwise the sign-in screen is unbranded on the
          device most people will use it on. */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 5 }}>
        <Link component={RouterLink} to="/" sx={{ display: 'flex', width: 'fit-content' }} aria-label="TopoSDS home">
          <Logo height={30} />
        </Link>
      </Box>

      <Box sx={{ my: 'auto', width: '100%', maxWidth: 420, mx: { xs: 0, md: 'auto' } }}>
        <Typography variant="h2" sx={{ mb: subtitle ? 1 : 4 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {subtitle}
          </Typography>
        )}

        {children}

        {footer && <Box sx={{ mt: 3 }}>{footer}</Box>}
      </Box>

      {/* Reachable on a phone too, where the panel's copy of these is not
          rendered. */}
      <Stack
        direction="row"
        gap={2.5}
        sx={{ display: { xs: 'flex', md: 'none' }, mt: 5, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
      >
        {[
          { to: '/support', label: 'Support' },
          { to: '/privacy', label: 'Privacy' },
          { to: '/terms', label: 'Terms' },
        ].map((link) => (
          <Link
            key={link.to}
            component={RouterLink}
            to={link.to}
            underline="hover"
            color="text.secondary"
            variant="body2"
          >
            {link.label}
          </Link>
        ))}
      </Stack>
    </Box>
  </Box>
);

export default AuthLayout;
